import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import NavBar from "./NavBar";

const API_BASE_URL = "https://api.itrustsecure.com";
function getToken() { return localStorage.getItem("itrust_token"); }
function authHeaders() { return { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` }; }

export default function HomePage({ user, onLogout }) {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [cityFilter, setCityFilter] = useState("");

  const loadData = useCallback(async () => {
    try {
      const [ordersRes, listingsRes] = await Promise.all([
        fetch(`${API_BASE_URL}/orders`, { headers: authHeaders() }),
        fetch(`${API_BASE_URL}/listings`, { headers: authHeaders() }),
      ]);
      const ordersData = await ordersRes.json();
      const listingsData = await listingsRes.json();
      if (ordersRes.ok) {
        const all = Array.isArray(ordersData) ? ordersData : [];
        setOrders(all.filter(o => o.buyerId === user?.id || o.sellerId === user?.id));
      }
      if (listingsRes.ok) setListings(Array.isArray(listingsData) ? listingsData : []);
    } catch (err) { console.warn(err); }
    finally { setLoading(false); }
  }, [user]);

  useEffect(() => { loadData(); }, [loadData]);

  const activeOrders = orders.filter(o => !["COMPLETED", "CANCELED", "REFUNDED"].includes(o.status));
  const pendingOrders = orders.filter(o => ["PENDING_ACCEPTANCE", "AWAITING_PAYMENT"].includes(o.status));
  const completedOrders = orders.filter(o => o.status === "COMPLETED");

  const myListings = listings.filter(l => l.sellerId === user?.id);
  const filteredListings = listings.filter(l =>
    l.status === "ACTIVE" &&
    l.sellerId !== user?.id &&
    l.items?.some(i => i.availableQuantity > 0) &&
    (search === "" || l.title.toLowerCase().includes(search.toLowerCase())) &&
    (cityFilter === "" || !l.city || l.city.toLowerCase().includes(cityFilter.toLowerCase()))
  );

  const styles = {
    root: { fontFamily: "'Segoe UI', Arial, sans-serif", background: "transparent", color: "#0f172a", minHeight: "100vh" },
    main: { maxWidth: 1200, margin: "0 auto", padding: "24px 20px" },
    statsGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, marginBottom: 24 },
    statCard: { background: "rgba(255,255,255,0.7)", border: "1px solid rgba(59,130,246,0.15)", borderRadius: 14, padding: 16, cursor: "pointer", backdropFilter: "blur(8px)", boxShadow: "0 2px 12px rgba(59,130,246,0.08)" },
    statLabel: { fontSize: 11, color: "#64748b", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 },
    statValue: (color) => ({ fontSize: 28, fontWeight: 800, color, marginBottom: 2 }),
    statSub: { fontSize: 11, color: "#94a3b8" },
    sectionHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 },
    sectionTitle: { fontSize: 22, fontWeight: 800, color: "#0f172a" },
    seeAll: { fontSize: 13, color: "#3b82f6", cursor: "pointer", fontWeight: 600 },
    searchBar: { display: "flex", gap: 12, marginBottom: 12, alignItems: "center", flexWrap: "wrap" },
    searchInput: { flex: 1, minWidth: 180, padding: "14px 18px", background: "rgba(255,255,255,0.8)", border: "1px solid rgba(59,130,246,0.2)", borderRadius: 14, color: "#0f172a", fontSize: 15, outline: "none", boxShadow: "0 2px 8px rgba(59,130,246,0.06)" },
    cityInput: { padding: "14px 18px", background: "rgba(255,255,255,0.8)", border: "1px solid rgba(59,130,246,0.2)", borderRadius: 14, color: "#0f172a", fontSize: 15, outline: "none", width: 160 },
    sellBtn: { background: "#3b82f6", color: "#fff", border: "none", padding: "14px 24px", borderRadius: 14, cursor: "pointer", fontWeight: 700, fontSize: 14, whiteSpace: "nowrap", boxShadow: "0 4px 12px rgba(59,130,246,0.3)" },
    listingsGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 12, marginBottom: 40 },
    listingCard: { background: "rgba(255,255,255,0.8)", border: "1px solid rgba(59,130,246,0.12)", borderRadius: 16, overflow: "hidden", cursor: "pointer", transition: "transform 0.2s, box-shadow 0.2s", backdropFilter: "blur(8px)", boxShadow: "0 2px 12px rgba(59,130,246,0.08)" },
    listingImg: { width: "100%", height: 220, objectFit: "cover", display: "block" },
    listingImgPlaceholder: { width: "100%", height: 220, background: "linear-gradient(135deg, rgba(59,130,246,0.08), rgba(124,58,237,0.08))", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 64 },
    listingBody: { padding: "14px 16px" },
    listingPrice: { fontSize: 22, fontWeight: 800, color: "#1e40af", marginBottom: 4 },
    listingTitle: { fontSize: 14, fontWeight: 600, color: "#1e293b", marginBottom: 4, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
    listingMeta: { fontSize: 12, color: "#64748b", marginBottom: 6 },
    listingLocation: { fontSize: 11, color: "#3b82f6", marginBottom: 6 },
    listingTags: { display: "flex", gap: 6, flexWrap: "wrap" },
    tag: (color) => ({ padding: "3px 8px", borderRadius: 999, background: `${color}18`, color, fontSize: 11, fontWeight: 700 }),
    orderCard: { background: "rgba(255,255,255,0.7)", border: "1px solid rgba(59,130,246,0.12)", borderRadius: 14, padding: 16, marginBottom: 10, cursor: "pointer", backdropFilter: "blur(8px)", boxShadow: "0 2px 8px rgba(59,130,246,0.06)" },
    orderRow: { display: "flex", justifyContent: "space-between", alignItems: "center" },
    orderId: { fontSize: 14, fontWeight: 700, marginBottom: 4, color: "#1e293b" },
    orderAmount: { fontSize: 18, fontWeight: 800, color: "#3b82f6" },
    orderStatus: (status) => {
      const colors = { COMPLETED: "#16a34a", DISPUTED: "#d97706", FUNDED: "#7c3aed", DELIVERED: "#7c3aed", CANCELED: "#6b7280", REFUNDED: "#dc2626" };
      const color = colors[status] || "#3b82f6";
      return { padding: "4px 10px", borderRadius: 999, background: `${color}15`, color, fontSize: 11, fontWeight: 700, border: `1px solid ${color}30` };
    },
    empty: { textAlign: "center", padding: "60px 20px", color: "#94a3b8", fontSize: 14 },
  };

  return (
    <div style={styles.root}>
      <NavBar user={user} onLogout={onLogout} />
      <div style={styles.main}>

        {/* Welcome Banner */}
        <div style={{ background: "linear-gradient(135deg, rgba(59,130,246,0.12) 0%, rgba(124,58,237,0.10) 100%)", border: "1px solid rgba(59,130,246,0.2)", borderRadius: 20, padding: "24px 28px", marginBottom: 24, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16, backdropFilter: "blur(8px)", boxShadow: "0 4px 20px rgba(59,130,246,0.1)" }}>
          <div>
            <div style={{ fontSize: 24, fontWeight: 800, marginBottom: 4, color: "#1e293b" }}>
              {user?.createdAt && new Date(user.createdAt) > new Date(Date.now() - 24 * 60 * 60 * 1000)
                ? `Welcome, ${user?.fullName?.split(" ")[0]}! 👋`
                : `Welcome back, ${user?.fullName?.split(" ")[0]} 👋`}
            </div>
            <div style={{ fontSize: 14, color: "#64748b" }}>Discover items and trade safely on ITrust Secure</div>
          </div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button style={{ background: "#3b82f6", color: "#fff", border: "none", padding: "10px 20px", borderRadius: 10, cursor: "pointer", fontWeight: 700, fontSize: 14, boxShadow: "0 4px 12px rgba(59,130,246,0.3)" }} onClick={() => navigate("/orders")}>
              📦 My Orders {activeOrders.length > 0 ? `(${activeOrders.length})` : ""}
            </button>
            <button style={{ background: "rgba(255,255,255,0.6)", color: "#1e40af", border: "1px solid rgba(59,130,246,0.2)", padding: "10px 20px", borderRadius: 10, cursor: "pointer", fontWeight: 700, fontSize: 14 }} onClick={() => navigate("/seller")}>
              🏪 Sell
            </button>
          </div>
        </div>

        {/* Stats */}
        <div style={styles.statsGrid}>
          <div style={styles.statCard} onClick={() => navigate("/orders")}>
            <div style={styles.statLabel}>Active Orders</div>
            <div style={styles.statValue("#3b82f6")}>{activeOrders.length}</div>
            <div style={styles.statSub}>In progress</div>
          </div>
          <div style={styles.statCard} onClick={() => navigate("/orders")}>
            <div style={styles.statLabel}>Pending Payment</div>
            <div style={styles.statValue("#d97706")}>{pendingOrders.length}</div>
            <div style={styles.statSub}>Awaiting action</div>
          </div>
          <div style={styles.statCard} onClick={() => navigate("/orders")}>
            <div style={styles.statLabel}>Completed</div>
            <div style={styles.statValue("#16a34a")}>{completedOrders.length}</div>
            <div style={styles.statSub}>All time</div>
          </div>
          <div style={styles.statCard} onClick={() => navigate("/seller")}>
            <div style={styles.statLabel}>My Listings</div>
            <div style={styles.statValue("#7c3aed")}>{myListings.length}</div>
            <div style={styles.statSub}>Active</div>
          </div>
        </div>

        {/* Recent Orders */}
        {orders.length > 0 && (
          <div style={{ marginBottom: 32 }}>
            <div style={styles.sectionHeader}>
              <div style={styles.sectionTitle}>Recent Orders</div>
              <span style={styles.seeAll} onClick={() => navigate("/orders")}>See all →</span>
            </div>
            {orders.slice(0, 2).map(order => (
              <div key={order.id} style={styles.orderCard} onClick={() => navigate(`/orders/${order.id}`)}>
                <div style={styles.orderRow}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "#1e293b", marginBottom: 2 }}>
                     {order.items?.[0]?.listingItem?.title || order.listing?.title || "Order"}
                     </div>
                     <div style={{ fontSize: 12, color: "#94a3b8" }}>
                      #{order.id.slice(0, 8)} · {order.sellerId === user?.id ? `Buyer: ${order.buyer?.fullName}` : `Seller: ${order.seller?.fullName}`}
                    </div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={styles.orderAmount}>${Number(order.totalAmount).toFixed(2)}</div>
                    <span style={styles.orderStatus(order.status)}>{order.status.replace(/_/g, " ")}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* My Listings */}
        {myListings.length > 0 && (
          <div style={{ marginBottom: 32 }}>
            <div style={styles.sectionHeader}>
              <div style={styles.sectionTitle}>My Listings</div>
              <span style={styles.seeAll} onClick={() => navigate("/seller")}>Manage →</span>
            </div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              {myListings.slice(0, 4).map(listing => (
                <div
                  key={listing.id}
                  onClick={() => navigate(`/listing-detail/${listing.id}`)}
                  style={{ background: "rgba(124,58,237,0.08)", border: "1px solid rgba(124,58,237,0.2)", borderRadius: 12, padding: "12px 16px", cursor: "pointer", minWidth: 160, backdropFilter: "blur(6px)" }}
                >
                  <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4, color: "#3730a3" }}>{listing.title}</div>
                  <div style={{ fontSize: 12, color: "#64748b" }}>{listing.items?.length || 0} items</div>
                  <div style={{ fontSize: 11, marginTop: 4, color: listing.status === "ACTIVE" ? "#16a34a" : "#6b7280", fontWeight: 700 }}>{listing.status}</div>
                </div>
              ))}
              {myListings.length > 4 && (
                <div onClick={() => navigate("/seller")} style={{ background: "rgba(255,255,255,0.6)", border: "1px solid rgba(59,130,246,0.15)", borderRadius: 12, padding: "12px 16px", cursor: "pointer", display: "flex", alignItems: "center", color: "#64748b", fontSize: 13 }}>
                  +{myListings.length - 4} more →
                </div>
              )}
            </div>
          </div>
        )}

        {/* Marketplace */}
        <div>
          <div style={styles.sectionHeader}>
            <div style={styles.sectionTitle}>Marketplace</div>
            <span style={{ fontSize: 13, color: "#64748b" }}>{filteredListings.length} listings</span>
          </div>

          <div style={styles.searchBar}>
            <input style={styles.searchInput} placeholder="🔍  Search listings, items, sellers..." value={search} onChange={e => setSearch(e.target.value)} />
            <input style={styles.cityInput} placeholder="📍 City" value={cityFilter} onChange={e => setCityFilter(e.target.value)} />
            <button style={styles.sellBtn} onClick={() => navigate("/seller")}>+ List Item</button>
          </div>

          {loading ? (
            <div style={styles.empty}><div style={{ fontSize: 40, marginBottom: 12 }}>⏳</div><div>Loading marketplace...</div></div>
          ) : filteredListings.length === 0 ? (
            <div style={styles.empty}>
              <div style={{ fontSize: 60, marginBottom: 16 }}>🛍️</div>
              <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8, color: "#475569" }}>No listings found</div>
              <div>{cityFilter ? `No listings in "${cityFilter}" — try a different city` : "Try a different search or be the first to list something"}</div>
            </div>
          ) : (
            <div style={styles.listingsGrid}>
              {filteredListings.map(listing => {
                const minPrice = listing.items?.length > 0 ? Math.min(...listing.items.map(i => Number(i.listedUnitPrice))) : 0;
                const availableItems = listing.items?.filter(i => i.availableQuantity > 0).length || 0;
                const isMine = listing.sellerId === user?.id;
                const firstPhoto = listing.items?.find(i => i.photoUrls?.length > 0)?.photoUrls?.[0];
                const hasOffers = listing.items?.some(i => i.isNegotiable);
                const totalItems = listing.items?.reduce((s, i) => s + i.availableQuantity, 0) || 0;
                const condition = listing.items?.[0]?.condition;

                return (
                  <div
                    key={listing.id}
                    style={styles.listingCard}
                    onClick={() => navigate(`/listing-detail/${listing.id}`)}
                    onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-4px)"; e.currentTarget.style.boxShadow = "0 8px 24px rgba(59,130,246,0.18)"; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 2px 12px rgba(59,130,246,0.08)"; }}
                  >
                    {firstPhoto ? (
                      <img src={firstPhoto} alt={listing.title} style={styles.listingImg} onError={e => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }} />
                    ) : null}
                    <div style={{ ...styles.listingImgPlaceholder, display: firstPhoto ? "none" : "flex" }}>🛍️</div>
                    <div style={styles.listingBody}>
                      <div style={styles.listingPrice}>
                        {minPrice > 0 ? `$${minPrice.toFixed(2)}` : "Free"}
                        {listing.items?.length > 1 && <span style={{ fontSize: 13, fontWeight: 500, color: "#94a3b8" }}> +</span>}
                      </div>
                      <div style={styles.listingTitle}>{listing.title}</div>
                      <div style={styles.listingMeta}>{listing.seller?.fullName} · {availableItems} available</div>
                      {listing.city && <div style={styles.listingLocation}>📍 {listing.city}{listing.state ? `, ${listing.state}` : ""}</div>}
                      <div style={styles.listingTags}>
                        {isMine && <span style={styles.tag("#7c3aed")}>Your Listing</span>}
                        {hasOffers && <span style={styles.tag("#16a34a")}>Offers OK</span>}
                        {totalItems <= 3 && totalItems > 0 && <span style={styles.tag("#d97706")}>Only {totalItems} left</span>}
                        {condition && condition !== "GOOD" && (
                          <span style={styles.tag(condition === "NEW" ? "#3b82f6" : condition === "LIKE_NEW" ? "#7c3aed" : condition === "FAIR" ? "#d97706" : "#ef4444")}>
                            {condition === "NEW" ? "🌟 New" : condition === "LIKE_NEW" ? "✨ Like New" : condition === "FAIR" ? "⚠️ Fair" : "🔧 Poor"}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}